import { Button, Card, Input } from "@omnichannel/ui";
import { useEffect, useRef, useState } from "react";
import { ApiError, request } from "./api";
import type { AppRow } from "./api";

type Group = "registered" | "wanted_free" | "not_interested" | "in_progress";
type TrialDay = {
  date: string;
  started: number;
  registered: number;
  wanted_free: number;
  not_interested: number;
  in_progress: number;
  conversion: number;
};
type TrialReport = { from: string; to: string; as_of: string; days: TrialDay[] };
type TrialDevice = { device_id: string; installed_at: string; last_activity_at: string | null };

const groups: { key: Group; label: string; color: string }[] = [
  { key: "registered", label: "Зарегистрировались", color: "#62c89b" },
  { key: "wanted_free", label: "Хотели бесплатно", color: "#e6b65c" },
  { key: "not_interested", label: "Не заинтересовались", color: "#e17c78" },
  { key: "in_progress", label: "В процессе", color: "#7898de" },
];
const groupLabels: Record<Group, string> = Object.fromEntries(groups.map((g) => [g.key, g.label])) as Record<Group, string>;
const todayUtc = () => new Date().toISOString().slice(0, 10);
const daysBefore = (day: string, count: number) => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - count);
  return date.toISOString().slice(0, 10);
};
const validDay = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
const validTimestamp = (value: string) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) && !Number.isNaN(Date.parse(value));

function isReport(value: unknown): value is TrialReport {
  if (!value || typeof value !== "object") return false;
  const report = value as Partial<TrialReport>;
  if (!Array.isArray(report.days) || !validDay(report.from ?? "") || !validDay(report.to ?? "") || !validTimestamp(report.as_of ?? "")) return false;
  let previousDate = "";
  return report.days.every((item) => {
    if (!item || typeof item !== "object") return false;
    const day = item as TrialDay;
    const counts = [day.started, day.registered, day.wanted_free, day.not_interested, day.in_progress];
    if (!validDay(day.date) || day.date < report.from! || day.date > report.to! || day.date <= previousDate) return false;
    previousDate = day.date;
    return counts.every((count) => Number.isSafeInteger(count) && count >= 0) &&
      day.registered + day.wanted_free + day.not_interested + day.in_progress === day.started &&
      typeof day.conversion === "number" && Number.isFinite(day.conversion) && day.conversion >= 0 && day.conversion <= 1 &&
      Math.abs(day.conversion - (day.started ? day.registered / day.started : 0)) < 1e-9;
  });
}

function isDeviceList(value: unknown): value is TrialDevice[] {
  return Array.isArray(value) && value.every((item) => {
    if (!item || typeof item !== "object") return false;
    const device = item as TrialDevice;
    return typeof device.device_id === "string" && /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(device.device_id) &&
      typeof device.installed_at === "string" && validTimestamp(device.installed_at) &&
      (device.last_activity_at === null || (typeof device.last_activity_at === "string" && validTimestamp(device.last_activity_at)));
  });
}

function rangeError(from: string, to: string) {
  if (!validDay(from) || !validDay(to)) return "Укажите обе даты периода в формате ГГГГ-ММ-ДД.";
  if (from > to) return "Начало периода должно быть не позже его окончания.";
  if (to === "9999-12-31") return "Последний день периода должен быть раньше 9999-12-31.";
  const count = (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000 + 1;
  if (count > 366) return "Период не может превышать 366 дней.";
  return "";
}

function queryFor(from: string, to: string) {
  return new URLSearchParams({ from, to }).toString();
}

function TrialsChart({ days }: { days: TrialDay[] }) {
  if (!days.length) return <p>Нет установок за выбранный период.</p>;
  const width = Math.max(560, 64 * days.length + 56);
  const height = 260;
  const chartTop = 24;
  const chartBottom = 204;
  const chartHeight = chartBottom - chartTop;
  const max = Math.max(1, ...days.map((day) => day.started));
  const ticks = [...new Set([0, Math.ceil(max / 2), max])];
  const barWidth = 28;
  const xFor = (index: number) => 42 + index * ((width - 72) / days.length) + (width - 72) / days.length / 2;
  const conversionPoints = days.map((day, index) => `${xFor(index)},${chartBottom - day.conversion * chartHeight}`).join(" ");
  const started = days.reduce((total, day) => total + day.started, 0);
  const registered = days.reduce((total, day) => total + day.registered, 0);
  return (
    <div className="trials-chart-scroll" style={{ overflowX: "auto" }}>
      <svg className="trials-chart" style={{ width: `${width}px`, minWidth: `${width}px`, height: `${height}px` }} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Пробные установки с ${days[0].date} по ${days[days.length - 1].date}: ${started} установок, ${registered} регистраций, общая конверсия ${started ? Math.round(registered / started * 100) : 0}%. Точные значения по дням приведены в таблице ниже.`}>
        <title>Группы устройств и конверсия по дате установки</title>
        <desc>Столбцы показывают количество устройств по группам, линия показывает долю зарегистрировавшихся за каждый день установки.</desc>
        {ticks.map((count) => {
          const y = chartBottom - count / max * chartHeight;
          return <g key={count}><line x1="34" x2={width - 16} y1={y} y2={y} stroke="var(--omni-border-subtle)" /><text x="2" y={y + 4} className="trials-axis-label">{count}</text></g>;
        })}
        {days.map((day, index) => {
          let offset = 0;
          const center = xFor(index);
          return <g key={day.date}>
            {groups.map((group) => {
              const value = day[group.key];
              const segmentHeight = value / max * chartHeight;
              const y = chartBottom - (offset + value) / max * chartHeight;
              offset += value;
              return <rect key={group.key} x={center - barWidth / 2} y={y} width={barWidth} height={segmentHeight} fill={group.color}>
                <title>{`${day.date}, ${group.label.toLocaleLowerCase("ru-RU")}: ${value}`}</title>
              </rect>;
            })}
            <text x={center} y={chartBottom + 20} textAnchor="middle" className="trials-axis-label">{day.date.slice(5)}</text>
          </g>;
        })}
        <polyline points={conversionPoints} fill="none" stroke="var(--omni-primary)" strokeWidth="3" />
        {days.map((day, index) => <circle key={day.date} cx={xFor(index)} cy={chartBottom - day.conversion * chartHeight} r="4" fill="var(--omni-primary)"><title>{`${day.date}, конверсия: ${Math.round(day.conversion * 100)}%`}</title></circle>)}
        <text x="4" y="16" className="trials-axis-label">Устройства</text>
        <text x={width - 56} y="16" className="trials-axis-label">Конверсия 0–100%</text>
      </svg>
    </div>
  );
}

function displayDate(value: string | null) {
  if (!value) return null;
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return null;
  return timestamp.toLocaleString("ru-RU", { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" }) + " UTC";
}

export function Trials() {
  const [apps, setApps] = useState<AppRow[]>([]);
  const [appsError, setAppsError] = useState("");
  const [appsLoading, setAppsLoading] = useState(true);
  const [appId, setAppId] = useState("");
  const [to, setTo] = useState(todayUtc);
  const [from, setFrom] = useState(() => daysBefore(todayUtc(), 29));
  const [group, setGroup] = useState<Group | "">("");
  const [page, setPage] = useState(0);
  const [report, setReport] = useState<TrialReport | null>(null);
  const [devices, setDevices] = useState<TrialDevice[]>([]);
  const [reportError, setReportError] = useState("");
  const [devicesError, setDevicesError] = useState("");
  const [reportLoading, setReportLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [reportRetryTick, setReportRetryTick] = useState(0);
  const [devicesRetryTick, setDevicesRetryTick] = useState(0);
  const [reportRequestKey, setReportRequestKey] = useState("");
  const [devicesRequestKey, setDevicesRequestKey] = useState("");
  const reportGeneration = useRef(0);
  const deviceGeneration = useRef(0);
  const appsGeneration = useRef(0);
  const invalidRange = rangeError(from, to);
  const cohortReady = !appsLoading && !appsError && !!appId && !invalidRange;
  const currentReportKey = JSON.stringify([appId, from, to, reportRetryTick]);
  const currentDevicesKey = JSON.stringify([appId, from, to, group, page, devicesRetryTick]);

  const loadApps = () => {
    const generation = ++appsGeneration.current;
    setAppsLoading(true);
    setAppsError("");
    request<AppRow[]>("/api/v1/apps").then((value) => {
      if (generation !== appsGeneration.current) return;
      if (!Array.isArray(value) || value.some((item) => !item || typeof item.id !== "string" || typeof item.name !== "string")) throw new Error("Некорректный список приложений");
      setApps(value);
      setAppId((current) => current && value.some((item) => item.id === current) ? current : (value[0]?.id ?? ""));
    }).catch((error) => { if (generation === appsGeneration.current) setAppsError(error instanceof ApiError ? error.message : "Не удалось загрузить приложения"); })
      .finally(() => { if (generation === appsGeneration.current) setAppsLoading(false); });
  };
  useEffect(() => { loadApps(); return () => { appsGeneration.current++; }; }, []);

  useEffect(() => {
    const generation = ++reportGeneration.current;
    setReportRequestKey(currentReportKey);
    setReport(null);
    setReportError("");
    if (!appId || invalidRange) {
      setReportLoading(false);
      return () => { reportGeneration.current++; };
    }
    setReportLoading(true);
    request<unknown>(`/api/v1/apps/${encodeURIComponent(appId)}/analytics/trials?${queryFor(from, to)}`)
      .then((value) => {
        if (generation !== reportGeneration.current) return;
        if (!isReport(value) || value.from !== from || value.to !== to) throw new Error("Сервер вернул некорректную аналитику пробных установок.");
        setReport(value);
      })
      .catch((error) => {
        if (generation === reportGeneration.current) setReportError(error instanceof ApiError ? error.message : error instanceof Error ? error.message : "Не удалось загрузить аналитику пробных установок");
      })
      .finally(() => { if (generation === reportGeneration.current) setReportLoading(false); });
    return () => { reportGeneration.current++; };
  }, [appId, from, to, invalidRange, reportRetryTick]);

  useEffect(() => {
    const generation = ++deviceGeneration.current;
    setDevicesRequestKey(currentDevicesKey);
    setDevices([]);
    setDevicesError("");
    if (!appId || invalidRange) {
      setDevicesLoading(false);
      return () => { deviceGeneration.current++; };
    }
    setDevicesLoading(true);
    const params = new URLSearchParams({ from, to, include_activity: "true", limit: "100", offset: String(page * 100) });
    if (group) params.set("group", group);
    request<unknown>(`/api/v1/apps/${encodeURIComponent(appId)}/analytics/trials/devices?${params.toString()}`)
      .then((value) => {
        if (generation !== deviceGeneration.current) return;
        if (!isDeviceList(value)) throw new Error("Сервер вернул некорректный список устройств.");
        setDevices(value);
      })
      .catch((error) => {
        if (generation === deviceGeneration.current) setDevicesError(error instanceof ApiError ? error.message : error instanceof Error ? error.message : "Не удалось загрузить устройства");
      })
      .finally(() => { if (generation === deviceGeneration.current) setDevicesLoading(false); });
    return () => { deviceGeneration.current++; };
  }, [appId, from, to, group, page, invalidRange, devicesRetryTick]);

  const retryReport = () => setReportRetryTick((value) => value + 1);
  const retryDevices = () => setDevicesRetryTick((value) => value + 1);

  return <main className="shell trials-page">
    <div className="shell-header"><div><h1>Пробные установки</h1><p className="meta">Анонимные устройства сгруппированы по первой установке. Email и контакты здесь не отображаются.</p></div></div>
    <Card className="trials-controls">
      <div className="trials-control">
        <label htmlFor="trials-app">Приложение</label>
        {appsLoading ? <p role="status">Загрузка приложений…</p> : appsError ? <p role="alert">{appsError} <Button onClick={loadApps}>Повторить</Button></p> : apps.length === 0 ? <p>Нет доступных приложений.</p> : <select id="trials-app" value={appId} onChange={(event) => { setPage(0); setAppId(event.target.value); }}>
          {apps.map((app) => <option key={app.id} value={app.id}>{app.name}</option>)}
        </select>}
      </div>
      <div className="trials-control"><label htmlFor="trials-from">Дата установки с</label><Input id="trials-from" type="date" value={from} onChange={(event) => { setPage(0); setFrom(event.target.value); }} /></div>
      <div className="trials-control"><label htmlFor="trials-to">по</label><Input id="trials-to" type="date" value={to} onChange={(event) => { setPage(0); setTo(event.target.value); }} /></div>
    </Card>
    {invalidRange && <p role="alert" className="error-text">{invalidRange}</p>}
    <section aria-labelledby="trials-chart-title" className="trials-section">
      <h2 id="trials-chart-title">Результат по дате установки</h2>
      {appsLoading ? <p role="status">Загрузка списка приложений…</p> : appsError ? <p>Загрузите список приложений, чтобы увидеть аналитику.</p> : !appId ? <p>Нет выбранного приложения.</p> : invalidRange ? <p>Исправьте период, чтобы увидеть график.</p> : reportRequestKey !== currentReportKey || reportLoading ? <p role="status">Загрузка аналитики…</p> : reportError ? <p role="alert" className="error-text">{reportError} <Button onClick={retryReport}>Повторить аналитику</Button></p> : report ? <>
        <div className="trials-legend" aria-label="Обозначения графика">{groups.map((item) => <span key={item.key}><i style={{ background: item.color }} />{item.label}</span>)}<span><i className="trials-line-key" />Конверсия регистрации</span></div>
        <TrialsChart days={report.days} />
        <p className="meta">Конверсия = регистрации / установки за соответствующий день. В процессе: {report.days.reduce((sum, day) => sum + day.in_progress, 0)}.</p>
        <div className="table-scroll"><table className="trials-days-table"><caption className="sr-only">Точные значения воронки по дням установки</caption><thead><tr><th scope="col">Дата</th><th scope="col">Начали</th><th scope="col">Зарегистрировались</th><th scope="col">Хотели бесплатно</th><th scope="col">Не заинтересовались</th><th scope="col">В процессе</th><th scope="col">Конверсия</th></tr></thead><tbody>{report.days.map((day) => <tr key={day.date}><th scope="row">{day.date}</th><td>{day.started}</td><td>{day.registered}</td><td>{day.wanted_free}</td><td>{day.not_interested}</td><td>{day.in_progress}</td><td>{Math.round(day.conversion * 100)}%</td></tr>)}</tbody></table></div>
      </> : null}
    </section>
    <section aria-labelledby="trials-devices-title" className="trials-section">
      <div className="trials-table-heading"><h2 id="trials-devices-title">Устройства группы</h2><label htmlFor="trials-group">Группа</label><select id="trials-group" value={group} disabled={!appId} onChange={(event) => { setPage(0); setGroup(event.target.value as Group | ""); }}><option value="">Все устройства</option>{groups.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}</select></div>
      {group && <p>Группа: {groupLabels[group]}.</p>}
      {appsLoading ? <p role="status">Загрузка списка приложений…</p> : appsError ? <p>Загрузите список приложений, чтобы увидеть устройства.</p> : !appId ? <p>Нет выбранного приложения.</p> : invalidRange ? <p>Исправьте период, чтобы увидеть устройства.</p> : devicesRequestKey !== currentDevicesKey || devicesLoading ? <p role="status">Загрузка устройств…</p> : devicesError ? <p role="alert" className="error-text">{devicesError} <Button onClick={retryDevices}>Повторить устройства</Button></p> : devices.length === 0 ? <p>В этой группе за выбранный период устройств нет.</p> : <>
        <div className="table-scroll"><table><caption className="sr-only">Анонимные устройства и время последней принятой активности</caption><thead><tr><th scope="col">Анонимный ID устройства</th><th scope="col">Дата установки</th><th scope="col">Последняя активность</th></tr></thead><tbody>{devices.map((device) => <tr key={device.device_id}><td><code>{device.device_id}</code></td><td>{displayDate(device.installed_at)}</td><td>{displayDate(device.last_activity_at) ?? <span>Нет сообщённой активности</span>}</td></tr>)}</tbody></table></div>
      </>}
      {cohortReady && devicesRequestKey === currentDevicesKey && !devicesError && (devices.length > 0 || page > 0) && <div className="trials-pagination"><Button variant="secondary" disabled={page === 0 || devicesLoading} onClick={() => setPage((value) => Math.max(0, value - 1))}>Предыдущие</Button><span>Страница {page + 1}</span><Button variant="secondary" disabled={devices.length < 100 || devicesLoading || page >= 1000} onClick={() => setPage((value) => value + 1)}>Следующие</Button></div>}
    </section>
  </main>;
}
